import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Clock, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/site-data";
import { submitContact, requestCallback } from "@/lib/api";

const initial = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [cbName, setCbName] = useState("");
  const [cbPhone, setCbPhone] = useState("");
  const [cbLoading, setCbLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and your message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you. We'll respond within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!cbName || !cbPhone) { toast.error("Name and phone required."); return; }
    setCbLoading(true);
    try {
      await requestCallback({ name: cbName, phone: cbPhone });
      toast.success("Callback requested. Our team will reach out shortly.");
      setCbName(""); setCbPhone("");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setCbLoading(false);
    }
  };

  return (
    <section data-testid="contact-section" id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">Contact</div>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Let's start a <span className="gold-gradient-text font-medium">conversation</span>.
            </h2>
            <p className="mt-5 text-[#4B5563] text-base leading-relaxed">
              Speak with our refining specialists about a quote, a facility visit or a long-term partnership.
            </p>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-heading text-sm font-medium text-[#0A0A0A]">Office</div>
                  <div className="text-sm text-[#4B5563] mt-0.5">{COMPANY.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-heading text-sm font-medium text-[#0A0A0A]">Phone</div>
                  <a href={`tel:${COMPANY.phone}`} className="text-sm text-[#4B5563] hover:text-[#D4AF37]">{COMPANY.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-heading text-sm font-medium text-[#0A0A0A]">Email</div>
                  <a href={`mailto:${COMPANY.email}`} className="text-sm text-[#4B5563] hover:text-[#D4AF37]">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-heading text-sm font-medium text-[#0A0A0A]">Hours</div>
                  <div className="text-sm text-[#4B5563]">{COMPANY.hours}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-lg overflow-hidden border border-[#E5E5E5]">
              <iframe
                title="Agroya Refinery Location"
                src="https://www.google.com/maps?q=Parwati+Industrial+Estate+Pune+Satara+Road+Pune+411019&output=embed"
                className="w-full h-56 grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <form onSubmit={submitCallback} className="mt-6 p-5 rounded-lg bg-[#F4F9FC] border border-[#E5E5E5]">
              <div className="font-heading text-sm font-medium text-[#0A0A0A] flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> Request a Callback
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Input data-testid="callback-name" placeholder="Name" value={cbName} onChange={(e) => setCbName(e.target.value)} className="bg-white" />
                <Input data-testid="callback-phone" placeholder="Phone" value={cbPhone} onChange={(e) => setCbPhone(e.target.value)} className="bg-white" />
              </div>
              <Button type="submit" data-testid="callback-submit" disabled={cbLoading} className="mt-3 w-full btn-gold h-10">
                {cbLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Callback"}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="bg-[#F4F9FC] p-8 lg:p-10 rounded-2xl border border-[#E5E5E5] grid gap-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name">Name *</Label>
                  <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-white" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <Label htmlFor="c-company">Company</Label>
                  <Input id="c-company" data-testid="contact-company" value={form.company} onChange={(e) => update("company", e.target.value)} className="bg-white" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label htmlFor="c-message">Message *</Label>
                <Textarea id="c-message" data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="bg-white" />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" data-testid="contact-submit" disabled={loading} className="btn-gold h-11 px-6">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Message"}
                </Button>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}?text=Hello%20Agroya%20Refinery%2C%20I%20have%20an%20enquiry.`}
                  target="_blank" rel="noreferrer"
                  data-testid="contact-whatsapp"
                  className="h-11 px-5 inline-flex items-center justify-center rounded-md border border-[#E5E5E5] hover:border-[#D4AF37] text-sm font-medium transition-colors"
                >
                  <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" strokeWidth={1.7} /> Chat on WhatsApp
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
